import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TimelineNode = ({
  stage = '',
  status = 'pending',
  timestamp = '',
  responsiblePerson = '',
  findings = '',
  modifications = [],
  attachments = [],
  communications = [],
  isLast = false,
  isExpanded = false,
  onToggle
}) => {
  const stageConfig = {
    recepcion: {
      label: 'Recepción',
      icon: 'ClipboardList',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary'
    },
    inspeccion: {
      label: 'Inspección',
      icon: 'Search',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      borderColor: 'border-accent'
    },
    cotizacion: {
      label: 'Cotización',
      icon: 'FileText',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      borderColor: 'border-secondary'
    },
    proveedores: {
      label: 'Proveedores',
      icon: 'Truck',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning'
    },
    aprobado: {
      label: 'Aprobado',
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success'
    },
    proceso: {
      label: 'En Proceso',
      icon: 'Wrench',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary'
    },
    terminado: {
      label: 'Terminado',
      icon: 'CheckCircle2',
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success'
    },
    entregado: {
      label: 'Entregado',
      icon: 'Package',
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success'
    }
  };

  const statusIcons = {
    completed: { icon: 'CheckCircle', color: 'text-success' },
    pending: { icon: 'Clock', color: 'text-warning' },
    'in-progress': { icon: 'Loader', color: 'text-primary' }
  };

  const config = stageConfig?.[stage] || stageConfig?.recepcion;
  const statusIcon = statusIcons?.[status] || statusIcons?.pending;

  return (
    <div className="relative flex gap-4 md:gap-6">
      <div className="flex flex-col items-center">
        <div
          className={`
            flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full
            ${config?.bgColor} ${config?.borderColor} border-2
            transition-smooth z-10
          `}
        >
          <Icon name={config?.icon} size={24} className={config?.color} />
        </div>
        {!isLast && (
          <div className="w-0.5 h-full bg-border mt-2" />
        )}
      </div>
      <div className="flex-1 pb-8 md:pb-12">
        <button
          onClick={onToggle}
          className="
            w-full text-left bg-card rounded-lg border border-border
            shadow-elevation-1 hover:shadow-elevation-2
            transition-smooth hover-lift press-effect focus-ring
            p-4 md:p-6
          "
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1">
                {config?.label}
              </h3>
              <div className="flex flex-wrap items-center gap-2 md:gap-3 text-sm caption text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Icon name={statusIcon?.icon} size={16} className={statusIcon?.color} />
                  <span className="capitalize">{status === 'in-progress' ? 'En Proceso' : status === 'completed' ? 'Completado' : 'Pendiente'}</span>
                </div>
                {timestamp && (
                  <>
                    <span className="hidden md:inline">•</span>
                    <div className="flex items-center gap-1.5">
                      <Icon name="Calendar" size={16} />
                      <span>{timestamp}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <Icon
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              size={20}
              className="text-muted-foreground flex-shrink-0"
            />
          </div>

          {responsiblePerson && (
            <div className="flex items-center gap-2 text-sm">
              <Icon name="User" size={16} className="text-muted-foreground" />
              <span className="caption text-muted-foreground">Responsable:</span>
              <span className="font-body font-medium text-foreground">{responsiblePerson}</span>
            </div>
          )}
        </button>

        {isExpanded && (
          <div className="mt-4 space-y-4 md:space-y-6">
            {findings && (
              <div className="bg-muted/50 rounded-lg p-4 md:p-6">
                <h4 className="text-base font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="FileText" size={18} className="text-primary" />
                  Hallazgos y Observaciones
                </h4>
                <p className="text-sm md:text-base font-body text-foreground leading-relaxed whitespace-pre-line">
                  {findings}
                </p>
              </div>
            )}

            {modifications?.length > 0 && (
              <div className="bg-warning/5 rounded-lg p-4 md:p-6 border border-warning/20">
                <h4 className="text-base font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="AlertTriangle" size={18} className="text-warning" />
                  Modificaciones Realizadas
                </h4>
                <div className="space-y-3">
                  {modifications?.map((mod, index) => (
                    <div key={index} className="flex flex-col gap-2 text-sm">
                      <div className="flex items-start gap-2">
                        <Icon name="Edit" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-body font-medium text-foreground">{mod?.field}</p>
                          <div className="flex flex-wrap items-center gap-2 caption text-muted-foreground mt-1">
                            <span className="line-through">{mod?.oldValue}</span>
                            <Icon name="ArrowRight" size={14} />
                            <span className="text-foreground font-medium">{mod?.newValue}</span>
                          </div>
                          {mod?.reason && (
                            <p className="caption text-muted-foreground mt-1">
                              <span className="font-medium">Motivo:</span> {mod?.reason}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {attachments?.length > 0 && (
              <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                <h4 className="text-base font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Paperclip" size={18} className="text-accent" />
                  Evidencia Fotográfica ({attachments?.length})
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {attachments?.map((attachment, index) => (
                    <div
                      key={index}
                      className="
                        relative aspect-square rounded-lg overflow-hidden
                        bg-muted border border-border
                        transition-smooth hover-lift
                        group cursor-pointer
                      "
                    >
                      <Image
                        src={attachment?.url}
                        alt={attachment?.alt}
                        className="w-full h-full object-cover"
                      />
                      <div className="
                        absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100
                        transition-smooth flex items-center justify-center
                      ">
                        <Icon name="ZoomIn" size={24} className="text-foreground" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/90 to-transparent p-2">
                        <p className="text-xs caption text-foreground truncate">
                          {attachment?.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {communications?.length > 0 && (
              <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                <h4 className="text-base font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="MessageSquare" size={18} className="text-secondary" />
                  Comunicaciones
                </h4>
                <div className="space-y-3">
                  {communications?.map((comm, index) => (
                    <div
                      key={index}
                      className="flex gap-3 p-3 rounded-lg bg-muted/30 border border-border"
                    >
                      <div className={`p-2 rounded-lg ${comm?.type === 'client' ? 'bg-primary/10' : 'bg-secondary/10'} flex-shrink-0`}>
                        <Icon
                          name={comm?.type === 'client' ? 'User' : 'Users'}
                          size={18}
                          className={comm?.type === 'client' ? 'text-primary' : 'text-secondary'}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-sm font-body font-medium text-foreground">
                            {comm?.recipient}
                          </span>
                          <span className="text-xs caption text-muted-foreground">
                            {comm?.timestamp}
                          </span>
                        </div>
                        <p className="text-sm font-body text-foreground">
                          {comm?.message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineNode;